import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import axios from 'axios';
import {ToastContainer, toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const RegistrationDetails = () => {
    const {eventId, userId} = useParams();
    const navigate = useNavigate();
    const [registration, setRegistration] = useState(null);
    const [eventQuestions, setEventQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        // Fetch both registration data and event questions when component mounts
        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch registration data
                const registrationResponse = await axios.get(`/otgservices/getRegistration.php?event_id=${eventId}&user_id=${userId}`);

                // Fetch event questions using your existing endpoint
                const questionsResponse = await axios.get(`/otgservices/getEventQuestions.php?event_id=${eventId}`);

                if (registrationResponse.data.success && registrationResponse.data.data) {
                    setRegistration(registrationResponse.data.data);
                    setFormData(registrationResponse.data.data);
                } else {
                    setError(registrationResponse.data.error || 'Failed to fetch registration data');
                    toast.error(registrationResponse.data.error || 'Failed to fetch registration data');
                }

                // Adapt this part to match your API response structure
                if (questionsResponse.data.questions) {
                    setEventQuestions(questionsResponse.data.questions);
                } else {
                    console.warn('No event questions found or questions not in expected format');
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Error connecting to the server. Please try again later.');
                toast.error('Error connecting to the server. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [eventId, userId]);

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Handle checkbox change (for initials)
    const handleCheckboxChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.checked,
        });
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!window.confirm('Are you sure you want to update this registration?')) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Add your update API endpoint here
            const response = await axios.post('/otgservices/UpdateRegistration.php', formData);

            if (response.data.success) {
                toast.success('Registration updated successfully!');
                // Update the local state with the updated data
                setRegistration(formData);
            } else {
                toast.error(response.data.error || 'Failed to update registration');
            }
        } catch (err) {
            console.error('Error updating registration:', err);
            toast.error('Error connecting to the server. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        navigate(-1); // Go back to previous page
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    if (error && !registration) {
        return (
            <div className="p-6 bg-gray-100 min-h-screen">
                <div className="max-w-4xl mx-auto bg-white p-6 rounded shadow">
                    <div className="text-red-500 mb-4">{error}</div>
                    <button
                        onClick={handleBack}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                    >
                        Back
                    </button>
                </div>
            </div>
        );
    }

    // Render a specific question field
    const renderQuestionField = (index) => {
        const question = eventQuestions[index - 1];
        const questionField = (
            <div key={`question-${index}`}>
                <label htmlFor={`question${index}`} className="block mb-1 text-sm font-medium">
                    {question ? question.question : `Question ${index}`}
                </label>
                <input
                    type="text"
                    id={`question_${index}`}
                    name={`question_${index}`}
                    value={formData[`question_${index}`]}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder={`Answer ${question ? 'this question' : `Question ${index}`}`}
                />
            </div>
        );

        return questionField;
    };

    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <ToastContainer/>
            <div className="max-w-4xl mx-auto bg-white p-6 rounded shadow">
                <h2 className="text-2xl font-bold mb-6 text-center">Registration Details</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Personal Information Section */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">Personal Information</h3>

                            <div>
                                <label className="block mb-1 text-sm font-medium" htmlFor="first_name">First
                                    Name</label>
                                <input
                                    id="first_name"
                                    name="first_name"
                                    type="text"
                                    value={formData.first_name || ''}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium" htmlFor="last_name">Last Name</label>
                                <input
                                    id="last_name"
                                    name="last_name"
                                    type="text"
                                    value={formData.last_name || ''}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium" htmlFor="phone_number">Phone
                                    Number</label>
                                <input
                                    id="phone_number"
                                    name="phone_number"
                                    type="text"
                                    value={formData.phone_number || ''}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>

                            <div>
                                <label htmlFor="initials" className="block mb-1 text-sm font-medium">
                                    <a
                                    href="https://www.overlandtrailguides.com/terms-of-use"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600">Overland Trail Guides Terms of Use</a>.
                                </label>
                                <input
                                    type="checkbox"
                                    id="initials"
                                    name="initials"
                                    checked={formData.initials}
                                    onChange={handleCheckboxChange}
                                    className="mr-2"
                                    required
                                />
                                <span>By checking this box I agree to the Terms of Use</span>
                            </div>
                        </div>

                        {/* Vehicle Information Section */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">Vehicle Information</h3>

                            <div>
                                <label className="block mb-1 text-sm font-medium" htmlFor="rig_make_model">Rig
                                    Make/Model</label>
                                <input
                                    id="rig_make_model"
                                    name="rig_make_model"
                                    type="text"
                                    value={formData.rig_make_model || ''}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium" htmlFor="truck_camper_description">Truck/Camper
                                    Description</label>
                                <textarea
                                    id="truck_camper_description"
                                    name="truck_camper_description"
                                    value={formData.truck_camper_description || ''}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded h-24"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium" htmlFor="travel_companions">Travel
                                    Companions</label>
                                <textarea
                                    id="travel_companions"
                                    name="travel_companions"
                                    value={formData.travel_companions || ''}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded h-16"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Emergency Contact Information */}
                    <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-4">Emergency Contact Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block mb-1 text-sm font-medium" htmlFor="emergency_contact_name">Contact
                                    Name</label>
                                <input
                                    id="emergency_contact_name"
                                    name="emergency_contact_name"
                                    type="text"
                                    value={formData.emergency_contact_name || ''}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>


                            <div>
                                <label className="block mb-1 text-sm font-medium" htmlFor="emergency_contact_phone">Phone
                                    Number</label>
                                <input
                                    id="emergency_contact_phone"
                                    name="emergency_contact_phone"
                                    type="text"
                                    value={formData.emergency_contact_phone || ''}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Medical Information */}
                    <div>
                        <label className="block mb-1 text-sm font-medium" htmlFor="medical_conditions">Medical
                            Conditions</label>
                        <textarea
                            id="medical_conditions"
                            name="medical_conditions"
                            value={formData.medical_conditions || ''}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded h-24"
                        />
                    </div>

                    {/* Event-specific Questions */}
                    {eventQuestions.length > 0 && (
                        <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                            <h3 className="font-medium text-lg mb-3">Event-Specific Questions</h3>
                            <div className="space-y-4">
                                {eventQuestions.map((q, idx) => renderQuestionField(idx + 1))}
                            </div>
                        </div>
                    )}

                    {/* Hidden fields for IDs */}
                    <input type="hidden" name="id" value={formData.id || ''}/>
                    <input type="hidden" name="user_id" value={formData.user_id || ''}/>
                    <input type="hidden" name="event_id" value={formData.event_id || ''}/>
                    <input type="hidden" name="usertype" value={formData.usertype || ''}/>

                    {/* Form buttons */}
                    <div className="flex justify-between mt-6">
                        <button
                            type="button"
                            onClick={handleBack}
                            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                        >
                            Back
                        </button>

                        <button
                            type="submit"
                            className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 disabled:bg-orange-300 disabled:cursor-not-allowed"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Updating...' : 'Update Registration'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RegistrationDetails;